import { useState, useEffect } from "react";
// @mui
import {
  Box,
  Card,
  IconButton,
  Stack,
  Typography,
  Tooltip,
  Divider,
  Avatar,
  Button,
} from "@mui/material";
// components
import Image from "../../../components/Image";
import SvgIconStyle from "../../../components/SvgIconStyle";
// hooks
import useResponsive from "../../../hooks/useResponsive";
import Iconify from "../../../components/Iconify";
// utils
import { fKrCandidateCardMeetingTimeKST } from "../../../utils/formatTime";

// ------------------------------------------------------------

export default function CandidateCardList({ jobPost, openJobPostOfferDialog, candidateId }) {
  const smUp = useResponsive("up", "sm");

  const [infoTooltipOpen, setInfoTooltipOpen] = useState(false);
  const [matchingCandidates, setMatchingCandidates] = useState([{}, {}, {}]);

  const inReview = ["review-requested", "write-requested"].includes(jobPost?.status);
  const MATCH_CAPACITY = Math.max(jobPost?.accept_capacity || 3, jobPost?.candidate_set?.length || 0);

  useEffect(() => {
    if (jobPost) {
      const candidates = Array(MATCH_CAPACITY).fill({});
      jobPost?.candidate_set?.slice(0, MATCH_CAPACITY).forEach((candidate, index) => {
        const candidateValue = candidate;
        const meetings = jobPost.meetings?.filter(m => m.status === 'confirm' && m?.attendees.startsWith(`crew. ${candidate.crew?.id}`));
        if (meetings?.length > 0) {
          const meetingTime = meetings?.[meetings.length - 1]?.start_time;
          candidateValue.meetingTime = meetingTime;
        };
        candidates[index] = candidateValue;
      });
      setMatchingCandidates(candidates);
      if (candidateId) {
        const index = jobPost.candidate_set.findIndex(c => c.id == candidateId);
        if (index >= 0) {
          openJobPostOfferDialog(jobPost.candidate_set[index]?.id);
        }
      }
    }
  }, [jobPost]);

  if (inReview) {
    return (
      <Stack
        sx={{
          borderRadius: 2,
          backgroundColor: "grey.100",
          pt: 3,
          pb: 0,
          mb: 5,
        }}
      >
        <Typography variant="h5" textAlign={"center"} paragraph>
          공고를 검토한 후 {!smUp && <br />}크루 3명을 매칭해 드립니다!
        </Typography>
        {smUp ? (
          <Image
            alt="continue-message"
            src="https://spacey-dio-public.s3.ap-northeast-2.amazonaws.com/images/job_post_in_review_guide.png"
            srcSet="
              https://spacey-dio-public.s3.ap-northeast-2.amazonaws.com/images/job_post_in_review_guide.png 1x,
              https://spacey-dio-public.s3.ap-northeast-2.amazonaws.com/images/job_post_in_review_guide_2x.png 2x,
              https://spacey-dio-public.s3.ap-northeast-2.amazonaws.com/images/job_post_in_review_guide_3x.png 3x,
            "
            sx={{
              mx: "auto",
            }}
          />
        ) : (
          <Image
            alt="continue-message"
            src="https://spacey-dio-public.s3.ap-northeast-2.amazonaws.com/images/job_post_in_review_guide_xs.png"
            srcSet="
              https://spacey-dio-public.s3.ap-northeast-2.amazonaws.com/images/job_post_in_review_guide_xs.png 1x,
              https://spacey-dio-public.s3.ap-northeast-2.amazonaws.com/images/job_post_in_review_guide_xs_2x.png 2x,
              https://spacey-dio-public.s3.ap-northeast-2.amazonaws.com/images/job_post_in_review_guide_xs_3x.png 3x,
            "
            sx={{
              mx: "auto",
            }}
          />
        )}
      </Stack>
    )
  } else {
    return (
      <Stack spacing={2} sx={{ mb: 7 }}>
        <Stack spacing={2} direction="row" alignItems="center" justifyContent="space-between">
          <Stack direction="row" spacing={1} alignItems="center">
            <SvgIconStyle src="/icons/icon-user-mono.svg" sx={{ width: 18, height: 18, color: "text.secondary" }} />
            <Typography variant="subtitle1">
              매칭된 크루
            </Typography>
            <Stack direction="row">
              <Typography variant="body2" sx={{ color: jobPost?.candidate_set?.length > 0 ? "oldprimary.main" : "text.disabled" }}>
                {jobPost?.candidate_set?.length || 0}명
              </Typography>
              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                /{MATCH_CAPACITY}명
              </Typography>
            </Stack>
            <Tooltip
              open={infoTooltipOpen}
              title={
                <Stack>
                  <Typography variant="caption">
                    - DIO에서 공고를 검토한 후 선정한 크루입니다.
                  </Typography>
                  <Typography variant="caption">
                    - 회사와 협업 의사가 있는 크루입니다.
                  </Typography>
                </Stack>
              }
              placement="bottom"
              arrow
              onOpen={() => setInfoTooltipOpen(true)}
              onClose={() => setInfoTooltipOpen(false)}
            >
              <IconButton
                size="small"
                sx={{ color: "text.disabled" }}
                onClick={() => setInfoTooltipOpen(true)}
              >
                <Iconify icon="eva:info-fill" sx={{ width: 16, height: 16 }} />
              </IconButton>
            </Tooltip>
          </Stack>
        </Stack>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: 'repeat(1, 1fr)',
              sm: 'repeat(auto-fill, minmax(350px, 1fr))',
            },
            gap: { xs: 2, sm: 2, db: 3 },
          }}
        >
          {matchingCandidates?.map((candidate, index) => {
            if (candidate?.crew) {
              return <CandidateCard key={index} candidate={candidate} openJobPostOfferDialog={openJobPostOfferDialog} />
            } else {
              return <FindingCard key={index} isStop={jobPost?.status === "stop"} />
            }
          })}
        </Box>
      </Stack>
    )
  }
};

function FindingCard({ isStop }) {
  return (
    <Card
      sx={{
        boxShadow: "none",
        border: "1px dashed #00000029",
        borderRadius: 2,
        width: 1,
        height: { xs: 120, sm: 188 },
        display: "flex",
        alignItems: "center",
      }}
    >
      <Stack
        direction="row" spacing={{ xs: 1.5, sm: 2 }} alignItems={"center"}
        sx={{
          p: { xs: 2.5, sm: 3 },
          width: 1,
        }}
      >
        <Avatar
          src={"https://spacey-dio-public.s3.ap-northeast-2.amazonaws.com/images/candidate_empty.png"}
          sx={{
            width: 56,
            height: 56,
          }}
        />
        {isStop ? (
          <Typography variant="subtitle2" sx={{ color: "text.disabled" }}>
            공고가 중단되어 매칭이 중단되었습니다.
          </Typography>
        ) : (
          <Stack>
            <Typography variant="subtitle2">
              공고에 적합한 크루를 찾고 있어요.
            </Typography>
            <Typography variant="caption" sx={{ color: "text.secondary" }}>
              크루가 심사에 통과하면 알려드릴게요.
            </Typography>
          </Stack>
        )}
      </Stack>
    </Card>
  )
}

function CandidateCard({ candidate, openJobPostOfferDialog }) {
  const [statusDescription, setStatusDescription] = useState('');
  const [buttonName, setButtonName] = useState('');
  const [hasBlur, setHasBlur] = useState(false);
  const [hasButton, setHasButton] = useState(true);
  const [hasMeeting, setHasMeeting] = useState(false);

  useEffect(() => {
    setButtonName("");
    setStatusDescription("");
    setHasButton(true);
    setHasBlur(false);
    const activeMeetings = candidate?.meet_calendars?.flatMap(cal =>
      cal.schedules?.filter(s => s.scheduled_at && !s.cancelled_at) || []
    );
    setHasMeeting(activeMeetings?.length > 0);

    if (candidate?.status === "offer-to-company") {
      setStatusDescription("크루와 매칭됐어요!");
      setButtonName("프로필 확인하기");
    } else if (candidate?.status === "waiting-3way-meeting") {
      setStatusDescription("크루가 미팅 시간을 확인할 거에요.");
      setHasButton(false);
    } else if (candidate?.status === "confirm-3way-meeting") {
      setStatusDescription("사전 미팅 일정을 잊지 마세요!");
      setButtonName("미팅 링크 확인하기");
    } else if (candidate?.status === "waiting-offer-from-crew") {
      setStatusDescription("크루가 곧 계약 조건을 보낼 거에요.");
      setHasButton(false);
    } else if (candidate?.status === "crew-negotiate-to-company") {
      setStatusDescription("크루가 최종 제안을 보냈어요.");
      setButtonName("제안 확인하기");
    } else if (candidate?.status == "accept") {
      setStatusDescription("크루와 계약을 진행하고 있어요!");
      setButtonName("자세히 보기");
    } else if (candidate?.status == "company-stop") {
      setHasBlur(true);
      setStatusDescription("공고가 중단되었습니다.");
      setButtonName("자세히 보기");
    } else if (candidate?.status === "crew-contract-no-response") {
      setHasBlur(true);
      setStatusDescription("크루가 제안하지 않아 매칭이 취소되었습니다.");
      setButtonName("자세히 보기");
    } else if (candidate?.status === "company-3way-no-response" || candidate?.status === "company-contract-no-response") {
      setHasBlur(true);
      setStatusDescription("결정 기한이 지나 매칭이 취소되었습니다.");
      setButtonName("자세히 보기");
    } else if (candidate?.status === "company-reject") {
      setHasBlur(true);
      setStatusDescription("크루와 매칭을 거절하셨습니다.");
      setButtonName("자세히 보기");
    } else if (candidate?.status === "company-negotiate-rewrite") {
      setStatusDescription("크루가 계약 조건을 다시 보낼 거에요.");
      setHasButton(false);
    } else if (candidate?.status === "reselect-3way-time") {
      setStatusDescription("크루가 미팅 시간을 다시 보냈어요!");
      setButtonName("미팅 예약하기");
    } else if (candidate?.status === "other-3way-time-requested") {
      setStatusDescription("크루가 다른 미팅 일정을 보낼 거에요.");
      setHasButton(false);
    }
  }, [candidate]);

  const meetingTimeStr = () => {
    const now = new Date();
    const activeMeetings = candidate?.meet_calendars?.flatMap(cal =>
      cal.schedules?.filter(s => s.scheduled_at && !s.cancelled_at) || []
    );
    if (activeMeetings?.length > 0) {
      const meetingTime = new Date(activeMeetings[0].scheduled_at);
      return `${fKrCandidateCardMeetingTimeKST(meetingTime)} 미팅 ${meetingTime > now ? "예약" : "완료"}`;
    } else if (candidate?.status === "company-reject" || candidate?.status === "company-3way-no-response") {
      return "미팅을 진행하지 않았습니다.";
    } else {
      return "미팅 예약 대기 중";
    }
  };

  return (
    <Card
      sx={{
        boxShadow: "none",
        border: "1px solid #0000003D",
        borderRadius: 2,
        width: 1,
        display: "flex",
        alignItems: "center",
        backgroundColor: hasBlur ? "common.white" : "#00000008",
        ":hover": {
          backgroundColor: "#0000000E",
          cursor: "pointer",
        },
        ":active": {
          backgroundColor: "#00000020",
        },
      }}
      onClick={() => openJobPostOfferDialog(candidate?.id)}
  >
      <Stack
        sx={{
          width: 1,
        }}
        divider={<Divider orientation="horizontal" flexItem />}
      >
        {/* crew */}
        <Stack direction="row" spacing={{ xs: 1.5, sm: 2 }} alignItems={"center"}
          sx={{
            p: { xs: 2, sm: 3 },
            pb: { xs: 1.5, sm: 2.5 },
            height: 1,
            backgroundColor: "common.white",
          }}
        >
          <Avatar
            src={`https://spacey-dio-public.s3.ap-northeast-2.amazonaws.com/crewji/crewji_${candidate?.crew?.randomhash}.png`}
            sx={{
              width: 64,
              height: 64,
              opacity: hasBlur ? 0.5 : 1,
              ...hasBlur && { filter: "grayscale(1)" }
            }}
          />
          <Stack spacing={0.75}>
            <Typography variant="subtitle2" sx={{ color: hasBlur ? "text.disabled" : "text.primary" }}>
              {candidate?.crew?.nickname || ''} 크루
            </Typography>
            <Stack spacing={.375}>
              <Stack direction="row" alignItems="center" spacing={.5}>
                <SvgIconStyle src="/icons/icon-bookmark-mono.svg" sx={{ width: 12, height: 12, color: hasBlur ? "text.disabled" : "text.secondary" }} />
                <Typography variant="caption" sx={{ color: hasBlur ? "text.disabled" : "text.secondary" }}>
                  {candidate?.crew?.role_name || candidate?.crew?.get_position_display}
                </Typography>
              </Stack>  
              <Stack direction="row" alignItems="center" spacing={.5}>
                <SvgIconStyle src="/icons/icon-building-mono.svg" sx={{ width: 12, height: 12, color: hasBlur ? "text.disabled" : "text.secondary" }} />
                <Typography variant="caption" sx={{ color: hasBlur ? "text.disabled" : "text.secondary" }}>
                  {candidate?.crew_careers?.length > 0 ? candidate?.crew_careers[0]?.summary : '-'}
                </Typography>
              </Stack>
              <Stack direction="row" alignItems="center" spacing={.5}>
                <SvgIconStyle src="/icons/ic_calendar.svg" sx={{ width: 12, height: 12, color: hasBlur ? "text.disabled" : hasMeeting ? "text.secondary" : "text.disabled" }} />
                <Typography variant="caption" sx={{ color: hasBlur ? "text.disabled" : hasMeeting ? "text.secondary" : "text.disabled"  }}>
                  {hasMeeting ? meetingTimeStr() : `미팅 예약 대기 중`}
                </Typography>
              </Stack>
            </Stack>
          </Stack>
        </Stack>
        {/* status & action */}
        <Stack
          sx={{
            px: { xs: 2.5, sm: 3 },
            py: { xs: 1.5, sm: 2 },
            height: { xs: 46, sm: 54 },
            minHeight: { xs: 46, sm: 54 },
            maxHeight: { xs: 46, sm: 54 },
            backgroundColor: hasBlur ? "common.white" : "inherit",
          }}
          direction="row"
          alignItems={"center"}
          justifyContent={hasButton ? "space-between" : "center" }
        >
          <Typography variant="caption" sx={{ color: hasBlur ? "text.secondary" : hasButton ? "text.primary" : "text.secondary" }}>
            {statusDescription}
          </Typography>
          {hasButton && (
            <Button
              disableRipple
              variant="text"
              size="small"
              endIcon={<Iconify icon="eva:arrow-ios-forward-fill" sx={{ width: 16, height: 16 }} />}
              sx={{
                color: hasBlur ? "text.secondary" : "inherit",
                "& .MuiButton-endIcon" : { ml: 0.5 }
              }}
            >
              {buttonName}
            </Button>
          )}
        </Stack>
      </Stack>
    </Card>
  )
}